import { EllipsisVerticalIcon } from "@heroicons/react/24/outline"
import { Checkbox, Dropdown, Spin } from "antd"
import { memo, useEffect, useRef, useState } from "react"
import tabletIcon from "assets/svg/tablet.svg"
import lecternIcon from "assets/svg/Lectern.svg"
import globeIcon from "assets/svg/globe.svg"
import maximizeIcon from "assets/svg/maximize-one.svg"
import arrowSquareOutIcon from "assets/svg/arrow-square-out.svg"
import { useAuthStore } from "stores"
import getIdFromKey from "utils/getIdFromKey"
import { ITimeSheetDocsEntity } from "../typings"
import { formatDay } from "pages/employee-dashboard/time-and-attendance/utils"

const VISIBLE_FLAGS = 2

interface ActivityRowProps {
  name: string
  clockIn: string
  clockOut: string
  methodLabel: string
  flags?: string[]
  reason?: string
  status?: number
  id: React.Key
  selectRow: (id: React.Key) => void
  isSelected: boolean
  viewUserProfile: () => void
  showModalReason: () => void
  acceptShiftPeriodModal: () => void
  rejectShiftPeriodModal: () => void
  rejectDetailsModal: () => void
  isLoading?: boolean
  timesheet: ITimeSheetDocsEntity
}

const TimeSheetActivityRow = memo(
  ({
    id,
    name,
    clockIn,
    clockOut,
    methodLabel,
    flags = [],
    reason,
    status,
    selectRow,
    isSelected,
    viewUserProfile,
    showModalReason,
    acceptShiftPeriodModal,
    rejectShiftPeriodModal,
    rejectDetailsModal,
    isLoading,
    timesheet,
  }: ActivityRowProps) => {
    const visibleFlags = flags.slice(0, VISIBLE_FLAGS)
    const overflowCount = Math.max(flags.length - VISIBLE_FLAGS, 0)
    const textRef = useRef<HTMLParagraphElement | null>(null)
    const [showButton, setShowButton] = useState(false)

    useEffect(() => {
      const el = textRef.current
      if (!el) return

      const id = requestAnimationFrame(() => {
        setShowButton(el.scrollHeight > el.clientHeight)
      })

      return () => cancelAnimationFrame(id)
    }, [reason])

    const { user } = useAuthStore((state) => state)

    const nextEntityId = timesheet?.nextReviewEntityId

    const nextRoleName = timesheet.nextReviewEntityRoleName?.toLowerCase() || ""

    const person_id = Number(getIdFromKey(user?.personKey || ""))

    const matchEntityId = nextEntityId != null && nextEntityId === person_id

    const matchRole =
      nextEntityId == null &&
      user?.userRoles?.some((role) => role.toLowerCase() === nextRoleName)

    const canApprove =
      user?.isPrimaryAdmin === true ||
      user?.isSecondaryAdmin === true ||
      matchEntityId ||
      matchRole

    return (
      <div className="w-full grid grid-cols-[240px_100px_120px_120px_120px_120px_160px_152px_248px_120px] h-36 items-center border-b border-b-gray-300">
        {/* User */}
        <div className="flex items-center gap-2 shrink-0">
          <Checkbox
            onChange={() => selectRow(id)}
            checked={isSelected}
          />
          <span className="rounded-full w-10 h-10 flex justify-center items-center bg-[#E2E8F0] p-1 font-semibold text-sm">
            {getInitials(name)}
          </span>
          <p
            className="truncate min-w-0 hover:underline hover:cursor-pointer"
            onClick={viewUserProfile}
          >
            {name}
          </p>
        </div>

        {/* Day */}
        <p>{formatDay(timesheet?.clockIn)}</p>

        {/* Shift Plan */}
        <p className="capitalize">{timesheet?.shiftPlan?.title}</p>

        {/* Shift Period */}
        <p className="capitalize">{timesheet?.shiftPeriod?.title}</p>

        {/* Time */}
        <TimeText value={clockIn} />
        <TimeText value={clockOut} />

        {/* Method */}
        <div className="flex items-center gap-2 whitespace-nowrap">
          <img
            width={15}
            height={15}
            src={
              methodLabel === "mobile-app"
                ? tabletIcon
                : methodLabel === "hardware-device"
                ? lecternIcon
                : globeIcon
            }
          />
          <span className="text-sm">
            {methodLabel === "mobile-app"
              ? "Mobile App"
              : methodLabel === "hardware-device"
              ? "Hardware Device"
              : "Web App"}
          </span>
        </div>

        {/* Flag */}
        <div className="flex flex-wrap gap-2 capitalize">
          {visibleFlags.map((flag, i) => (
            <PillBadge key={i}>{flag}</PillBadge>
          ))}
          {overflowCount > 0 && (
            <button
              type="button"
              onClick={viewUserProfile}
              className="rounded-full py-1 px-3 border-2 border-[#E2E8F0] whitespace-nowrap flex items-center gap-1 cursor-pointer normal-case"
            >
              <span className="underline">
                +{overflowCount} flag{overflowCount > 1 ? "s" : ""}
              </span>
              <img
                src={arrowSquareOutIcon}
                width={12}
                height={12}
                alt=""
              />
            </button>
          )}
        </div>

        {/* Reason */}
        <div className="flex items-center px-4 flex-wrap">
          <p
            ref={textRef}
            className="min-w-0 line-clamp-3 break-all"
          >
            {reason}
          </p>
          {showButton && (
            <span
              className="flex text-sm items-center gap-2 bg-[#F1F5F9] px-3 py-1 rounded-full w-fit cursor-pointer"
              onClick={showModalReason}
            >
              {" "}
              <img
                src={maximizeIcon}
                width={10}
                height={10}
              />
              Expand
            </span>
          )}
        </div>

        {/* Action */}
        <div className="shrink-0 w-fit">
          <>
            {status === 1 ? (
              <p className="text-green-600">Approved</p>
            ) : status === 2 ? (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "1",
                      label: "View rejection details",
                      onClick: rejectDetailsModal,
                    },
                    // {
                    //   key: "2",
                    //   label: "Change to Approved",
                    //   onClick: acceptShiftPeriodModal,
                    // },
                  ],
                }}
              >
                <button className="flex items-center gap-1">
                  <span className="text-red-600">Rejected</span>
                  <EllipsisVerticalIcon className="w-6 h-6 text-[#004AF5]" />
                </button>
              </Dropdown>
            ) : (
              <>
                {isLoading ? (
                  <Spin>
                    <div className="h-[94px] w-full bg-white rounded-lg border" />
                  </Spin>
                ) : (
                  <>
                    {canApprove && (
                      <div className="flex gap-2 items-center justify-center">
                        <button
                          onClick={acceptShiftPeriodModal}
                          className="border border-[#E2E8F0] w-[35px] h-[35px] rounded-md flex justify-center items-center hover:border-green transition-all"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            className="w-5 h-5 hover:text-green transition-all"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="m4.5 12.75 6 6 9-13.5"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={rejectShiftPeriodModal}
                          className="border border-[#E2E8F0] w-[35px] h-[35px] rounded-md flex justify-center items-center hover:border-red-500 transition-all"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            className="w-5 h-5 hover:text-red-500 transition-all"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 18 18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    )}
                    <p className="font-bold text-sm text-slate-900">
                      {timesheet.reviewStatus}
                    </p>
                  </>
                )}
              </>
            )}
          </>
        </div>
      </div>
    )
  }
)

export default TimeSheetActivityRow

export const getInitials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()

export const TimeText = ({ value }: { value: React.ReactNode }) => (
  <p className="whitespace-nowrap">{value}</p>
)
export const PillBadge = ({ children }: { children?: React.ReactNode }) => (
  <span className="rounded-full py-1 px-3 border border-[#E2E8F0] whitespace-nowrap capitalize">
    {children}
  </span>
)
